/**
 * SMS Consent API
 *
 * Self-service opt-in/opt-out only. There is deliberately no admin variant —
 * A2P 10DLC consent must come from the person holding the handset, so every
 * call here acts on the authenticated user's own record.
 */

import { api } from './client';

// =============================================================================
// Types
// =============================================================================

export interface SmsConsentStatus {
  /** True only when verified + consented + not opted out. */
  enabled: boolean;
  /** e.g. "(•••) •••-5198". Null when no number is on file. */
  maskedPhone: string | null;
  /** A number was submitted but the passcode hasn't been entered yet. */
  awaitingVerification: boolean;
  optedOut: boolean;
  consentedAt: string | null;
  consentVersion: string | null;
  /** An admin typed in a contact number, which is never usable for SMS. */
  hasUnverifiedContactPhone: boolean;
}

export interface StartConsentResponse {
  message: string;
  maskedPhone: string;
  /** True when Twilio Verify isn't configured — dev only, code is 000000. */
  devMode: boolean;
}

export interface ConfirmConsentResponse {
  message: string;
  enabled: boolean;
  maskedPhone: string;
}

// =============================================================================
// API Functions
// =============================================================================

export async function getSmsConsentStatus(): Promise<SmsConsentStatus> {
  const response = await api.get<SmsConsentStatus>('/sms-consent/status');
  return response.data;
}

/**
 * Submit a number and the consent checkbox. Sends a one-time passcode.
 * `consent` must be true — the server rejects anything else.
 */
export async function startSmsConsent(
  phone: string,
  consent: boolean
): Promise<StartConsentResponse> {
  const response = await api.post<StartConsentResponse>('/sms-consent/start', {
    phone,
    consent,
  });
  return response.data;
}

/** Enter the passcode. This is the call that actually records consent. */
export async function confirmSmsConsent(code: string): Promise<ConfirmConsentResponse> {
  const response = await api.post<ConfirmConsentResponse>('/sms-consent/confirm', { code });
  return response.data;
}

export async function optOutOfSms(): Promise<{ message: string; enabled: boolean }> {
  const response = await api.post<{ message: string; enabled: boolean }>('/sms-consent/opt-out');
  return response.data;
}
